/**
 * NZSC-3236: 자산원클릭(셜록) 영업용 "견적받기" 리드 수집
 *
 * 랜딩 히어로 drag-drop 존에서 올린 파일(단일)을 페이지 전역 상태로 보관 →
 * 견적 CTA → 회원방식 팝업(601) 에서 회원/비회원 분기.
 *   - 비회원: 발급 팝업(573)에서 이메일/전화번호 입력 후 파일과 함께 제출.
 *   - 회원(로그인): 계정 이메일/전화로 즉시 제출.
 *   - 회원(비로그인): 가입 완료 후 신규 계정 이메일/전화로 제출(대기 플래그 사용).
 *
 * BE 계약(고정): POST /api/v1/sherlock/lead, multipart/form-data
 *   memberType="회원"|"비회원", email(선택), phone(선택), file(단일, 선택). 성공 판정 res.ok(truthy).
 *   email/phone 은 옵셔널 — 빈값이면 아예 미전송(BE 가 "없음" 처리).
 */
import SherlockAcConstant from "@/mixins/sherlock/const/sherlockAcConstant.js";
import { callPostFormDataApi } from "@/utils/apiWrapper";

// 회원 리드 대기 플래그 키(sessionStorage 미러). 비로그인 "회원으로 받기" → 가입 완료 후 제출 예약.
// 플래그는 새로고침(풀리로드)까지 넘기되, File 은 직렬화 불가라 리로드 시 소실됨(최선노력).
const PENDING_MEMBER_LEAD_KEY = "sherlock_pending_member_lead";

export const state = {
  _leadFile: null, // 히어로 드롭존에서 올린 File 객체 (단일)
  _leadFileName: "", // 화면 노출용 파일명
  // 비로그인 회원경로 대기 플래그. 앱 부팅 시 sessionStorage 에서 복원(리로드 대비).
  _pendingMemberLead: sessionStorage.getItem(PENDING_MEMBER_LEAD_KEY) === "1",
};

export const mutations = {
  SET_LEAD_FILE(state, file) {
    state._leadFile = file || null;
    state._leadFileName = file ? file.name : "";
  },
  // 회원 리드 대기 플래그 세팅/해제. 반응형 state + sessionStorage 동시 갱신.
  SET_PENDING_MEMBER_LEAD(state, val) {
    state._pendingMemberLead = !!val;
    if (val) sessionStorage.setItem(PENDING_MEMBER_LEAD_KEY, "1");
    else sessionStorage.removeItem(PENDING_MEMBER_LEAD_KEY);
  },
  RESET_LEAD(state) {
    state._leadFile = null;
    state._leadFileName = "";
    state._pendingMemberLead = false;
    sessionStorage.removeItem(PENDING_MEMBER_LEAD_KEY);
  },
};

export const getters = {
  hasLeadFile: state => !!state._leadFile,
  leadFileName: state => state._leadFileName,
  isPendingMemberLead: state => state._pendingMemberLead,
};

export const actions = {
  /**
   * 리드 제출. FormData 조립 후 multipart POST.
   * @param {Object} payload
   * @param {string} [payload.email] 이메일 (없거나 빈값이면 미전송)
   * @param {string} [payload.phone] 전화번호 (없거나 빈값이면 미전송)
   * @param {string} [payload.memberType] "회원"|"비회원" (기본 "비회원")
   * @returns {Promise<boolean>} 성공 여부 (res.ok truthy → true)
   */
  async acSubmitSherlockLead({ state }, { email, phone, memberType = "비회원" } = {}) {
    const formData = new FormData();
    formData.append("memberType", memberType);
    // email/phone 은 옵셔널 — 값이 있을 때만 첨부(빈값은 BE 가 "없음" 처리하도록 미전송).
    if (email) formData.append("email", email);
    if (phone) formData.append("phone", phone);
    // 파일은 히어로 드롭존에서 보관한 단일 File. 없을 수도 있으므로 존재 시에만 첨부.
    if (state._leadFile) {
      formData.append("file", state._leadFile);
    }
    const res = await callPostFormDataApi({
      url: SherlockAcConstant.POST_SHERLOCK_LEAD,
      formData,
    });
    return !!(res && res.ok);
  },

  /**
   * 비로그인 회원경로 대기 리드 제출(가입 완료 직후 호출).
   * 대기 플래그가 있을 때만 신규 계정 email/phone + 보관 파일로 회원 리드 제출 후 플래그/파일 정리.
   * @returns {Promise<boolean>} 제출 시도했고 성공했으면 true, 대기 없음/실패면 false.
   */
  async acSubmitPendingMemberLead({ state, dispatch, commit }, { email, phone } = {}) {
    if (!state._pendingMemberLead) return false;
    let ok = false;
    try {
      ok = await dispatch("acSubmitSherlockLead", { email, phone, memberType: "회원" });
    } catch (e) {
      ok = false;
    }
    // 성공/실패와 무관하게 대기 상태·보관 파일은 정리(가입 완료 흐름을 막지 않음).
    commit("RESET_LEAD");
    return ok;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
